import {create} from 'zustand'
import {devtools} from 'zustand/middleware'

import { 
    ICommercialOffersState, ICommercialOffersActions,
    ICommercialOffersList, ICommercialOfferCard,
    ICommercialOfferCardSendedData,
    ICommercialOfferCardProgramsSendedData,
    CommercialOfferCardFilesListType,
} from "./state.types"

import { 
    getCommercialOffers,
    getCommercialOfferCard,
    setCommercialOfferCardSave,
    setCommercialOfferProgramCardDelete,
    setCommercialOfferProgramCardSave,
    setCommercialOfferUploadFiles,
    getCommercialOfferFiles,
    setCommercialOfferDeleteFiles,
    setCommercialOfferGenerateDocument,
    sendTelegramMessageClientKP,
} from './api'
import { createLatestRequestController } from '../latest-request'


export const initialStateCommercialOffers: ICommercialOffersState = {
    error: null,
    isReloadCommercialOffers: false,
    isLoadingCard: false,
 
    commercialOffers: {
        isLoading: false,

        items: [] as ICommercialOffersList[],
        totalCount: 0,
        pageSize: 0,
        currentPage: 0,

        created: 0,
        delayedCall: 0,
        sended: 0,
        executionContract: 0,
        canceled: 0
    },
}

const commercialOffersListRequest = createLatestRequestController()

/** Хранилище страницы - Коммерческие предложения */
export const useCommercialOffers = create<ICommercialOffersState & ICommercialOffersActions>()(devtools((set) => ({
    ...initialStateCommercialOffers,

    /** Сброс хранилища коммерческих предложений до начального состояния */
    clearCommercialOffersState: () => {
        commercialOffersListRequest.invalidate()
        set(initialStateCommercialOffers) 
    },

    

    /** Основной список коммерческих предложений */
    actionGetCommercialOffers: async (pageNumber: number, search: string, accessSites?: string) => {
        const request = commercialOffersListRequest.start()

        set((state) => ({ 
            commercialOffers: { 
                ...state.commercialOffers, 
                isLoading: !state.isReloadCommercialOffers 
            } 
        }))

        /** Выполняем запрос */
        const response = await getCommercialOffers(pageNumber, search, accessSites, request.signal)
        if (!request.isCurrent()) return response
        request.finish()

        // Проверка статуса
        if (response.status === 200) {
            set({error: null})
            set({isReloadCommercialOffers: false})

            set((state) => ({
                commercialOffers: {
                    ...state.commercialOffers, 
                    items: response.data!.items,
                    totalCount: response.data!.totalCount,
                    pageSize: response.data!.pageSize,
                    currentPage: response.data!.currentPage,

                    created: response.data!.created,
                    delayedCall: response.data!.delayedCall,
                    sended: response.data!.sended,
                    executionContract: response.data!.executionContract,
                    canceled: response.data!.canceled,
                }
            }))
        }

        set((state) => ({ 
            commercialOffers: { 
                ...state.commercialOffers, 
                isLoading: false 
            } 
        }))

        return response
    },




    /** Получение данных по анкете коммерческого предложения */
    actionGetCommercialOfferCard: async (commercialOfferID: number) => {
        let returnData: ICommercialOfferCard | null = null
        set({ isLoadingCard: true })

        const response = await getCommercialOfferCard(commercialOfferID)
        if (response.status !== 200) set({ error: { type: response.status === 500 ? 'error' : 'warning', message: response.message } })

        if (response.message === 'OK') {
            set({error: null})

            if (response.data) returnData = response.data
        }

        set({ isLoadingCard: false })

        return returnData
    },
    /** Получение файлов коммерческого предложения */
    actionGetCommercialOfferFiles: async (listenerID: number) => {
        let returnData: CommercialOfferCardFilesListType[] = []
        set({isLoadingCard: true})

        const response = await getCommercialOfferFiles(listenerID)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.message === 'OK') {
            set({error: null})

            if (response.data) returnData = response.data?.files
        }

        set({isLoadingCard: false})
        return returnData
    },

    /** Сохранение данных по анкете коммерческого предложения */
    actionSetCommercialOfferCard: async (commercialOfferID: number, data: ICommercialOfferCardSendedData) => {
        set({isLoadingCard: true})

        try {
            const response = await setCommercialOfferCardSave(commercialOfferID, data)
            if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

            if (response.message === 'OK') {
                set({ error: null })
                set({isReloadCommercialOffers: true})
            }

            return response
        } finally {
            set({isLoadingCard: false})
        }
    },

    /** Удаление программы из коммерческого предложения */
    actionSetCommercialOfferProgramCardDelete: async (commercialOfferProgramID: number) => {
        set({isLoadingCard: true})

        const response = await setCommercialOfferProgramCardDelete(commercialOfferProgramID)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.message === 'OK') set({ error: null })
            
        set({isLoadingCard: false})
        return response.status
    },

    /** Добавление/Редактирование программы по коммерческому предложению */
    actionSetCommercialOfferProgramCardSave: async (commercialOfferID: number, data: ICommercialOfferCardProgramsSendedData) => {
        set({isLoadingCard: true})

        const response = await setCommercialOfferProgramCardSave(commercialOfferID, data)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.message === 'OK') set({ error: null })
            
        set({isLoadingCard: false})
        return response
    },

    /** Загрузка файлов коммерческого предложения на сервер */
    actionSetCommercialOfferUploadFiles: async (commercialOfferID: number, files: File[]) => {
        set({isLoadingCard: true})

        const response = await setCommercialOfferUploadFiles(commercialOfferID, files)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.message === 'OK') {
            set({ error: null })
            // set({isReloadListeners: true})
        }

        set({isLoadingCard: false})
    },

    /** Удаление файлов по коммерческому предложению */
    actionSetCommercialOfferDeleteFiles: async (commercialOfferID: number, fileID: number) => {
        set({isLoadingCard: true})

        const response = await setCommercialOfferDeleteFiles(commercialOfferID, fileID)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.message === 'OK') {
            set({ error: null })
            // set({isReloadListeners: true})
        }
            
        set({isLoadingCard: false})
    },

    

    /** Формирование документов по коммерческому предложению */
    actionSetCommercialOfferGenerateDocument: async (commercialOfferID: number, documents: any[]) => {
        set({isLoadingCard: true})

        const response = await setCommercialOfferGenerateDocument(commercialOfferID, documents)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.message === 'OK') set({ error: null })
            
        set({isLoadingCard: false})
        return response.status
    },

    /** Отправка сообщений в telegram канал (ИКТ - КП клиенту) */
    actionSendTelegramMessageClientKP: async (commercialOfferID: number) => {
        set({isLoadingCard: true})

        const response = await sendTelegramMessageClientKP(commercialOfferID)
        if (response.status !== 200) set({ error: {type: response.status === 500 ? 'error' : 'warning', message: response.message} })

        if (response.status === 200) {}

        set({isLoadingCard: false})

        return response
    },

}), {name: 'StateCommercialOffers'}))